import { useNavigate } from "react-router-dom";
import { ActionIcon, Button, Center, Container, Divider, Group, Loader, Menu, Paper, ScrollArea, SimpleGrid, Space, Stack, Text, ThemeIcon, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBuildingStore, IconChevronLeft, IconDots, IconExternalLink, IconPencil, IconPhone, IconTrash } from "@tabler/icons-react";
import moment from "moment";

import { useAccess } from "../../Auth/auth-functions";
import { AzureADRoles } from "../../Auth/auth-roles";
import { VendorDto } from "../../models/vendors.models";
import { ListWorkOrderDto } from "../../models/workorder.models";
import { CrudUserDate } from "../Shared/CrudUserDate";
import { VendorWorkOrderList } from "./VendorWorkOrderList";
import { VendorAssetsList } from "./VendorAssetsList";
import classes from "./VendorDetailView.module.css";

interface VendorDetailViewProps {
	vendor?: VendorDto | null;
	isLoading?: boolean;
	onEdit?: () => void;
	onDelete?: (vendor: VendorDto) => void;
	onClose?: () => void;
}

export const VendorDetailView = ({ vendor, isLoading, onEdit, onDelete, onClose }: VendorDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditVendor = useAccess(AzureADRoles.Admin);

	const handleWorkOrderSelected = (workOrder: ListWorkOrderDto): void => {
		navigate(`/workorders/${workOrder.WorkOrderId}`);
	};

	const deleteVendor = () => {
		if (!vendor) return;

		if (onDelete && vendor) {
			onDelete(vendor);
		}
	};

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)" }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>;
	if (!vendor) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)" }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconBuildingStore size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select a vendor to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>;

	return <>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? "calc(100vh)" : "calc(100vh - 160px)" }}>
				<Stack gap={0} mb="md" justify="center" className={classes["header"]}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
								<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3}>{vendor?.Name}</Title>
								<Text fz="xs" c="dimmed">Created on {moment(vendor?.Created).format("MMMM D, YYYY")}</Text>
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{vendor.Deleted == null && <>
									{!isMobile &&
										<Button onClick={onEdit} disabled={!canEditVendor} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
									}
									{isMobile &&
										<Button onClick={onEdit} disabled={!canEditVendor} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
									}
									<Divider orientation="vertical" />
									<Menu position="bottom-end" shadow="md" radius="lg">
										<Menu.Target>
											<Button disabled={!canEditVendor} variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
										</Menu.Target>
										<Menu.Dropdown>
											<Menu.Item onClick={deleteVendor} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								</>}
							</Button.Group>
						</Group>
					</Group>
				</Stack>

				<Container px="20">
					{vendor?.Description && <>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Description</Text>
							<Text fz="sm">{vendor?.Description}</Text>
						</Stack>
						<Divider my="md" />
					</>}

					{(vendor?.VendorType || vendor?.Account) && <>
					<SimpleGrid cols={isMobile ? 1 : 2} mb="md">
						{vendor?.VendorType && <>
							<Stack gap="xs">
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Vendor Type</Text>
								<Text fz="sm">{vendor?.VendorType}</Text>
							</Stack>
						</>}
						{vendor?.Account && <>
							<Stack gap="xs">
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Account #</Text>
								<Text fz="sm">{vendor?.Account}</Text>
							</Stack>
						</>}
					</SimpleGrid>
					</>}

					{vendor?.Address && <>
						<Stack gap="xs" mb="md">
							<Group gap="5">
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Address</Text>
								<Tooltip label="Open in Google Maps" withArrow position="top">
									<ActionIcon
										component="a"
										href={`https://www.google.com/maps/search/${encodeURIComponent(vendor.Address!)}`}
										target="_blank"
										rel="noopener noreferrer"
										variant="subtle"
										size="md"
										m={-4}
									>
										<IconExternalLink size={20} />
									</ActionIcon>
								</Tooltip>
							</Group>
							<Text fz="sm">{vendor?.Address}</Text>
						</Stack>
					</>}

					{(vendor?.Phone || vendor?.Website) && <>
						<SimpleGrid cols={isMobile ? 1 : 2}>
							{vendor?.Phone && <Stack gap="xs">
							<Group gap="5">
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Phone</Text>
								<Tooltip label="Call" withArrow position="top">
									<ActionIcon
										component="a"
										href={`tel:${vendor.Phone}`}
										variant="subtle"
										size="md"
										m={-4}
									>
										<IconPhone size={20} />
									</ActionIcon>
								</Tooltip>
							</Group>
								<Text fz="sm">{vendor?.Phone}</Text>
							</Stack>}
							{vendor?.Website && <Stack gap="xs">
							<Group gap="5">
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Website</Text>
								<Tooltip label="Open website" withArrow position="top">
									<ActionIcon
										component="a"
										href={vendor.Website.match(/^https?:\/\//) ? vendor.Website : `https://${vendor.Website}`}
										target="_blank"
										rel="noopener noreferrer"
										variant="subtle"
										size="md"
										m={-4}
									>
										<IconExternalLink size={20} />
									</ActionIcon>
								</Tooltip>
							</Group>
								<Text fz="sm">{vendor?.Website}</Text>
							</Stack>}
						</SimpleGrid>
						<Divider my="md" />
					</>}

					<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Contacts</Text>
					<Space h="xs" />
					{vendor.Contacts?.length > 0 ? (
						<Stack gap="sm">
							{vendor.Contacts.map((contact, index) => (
								<Paper key={`${contact.FullName}-${index}`} withBorder p="sm" radius="md">
									<Group justify="space-between" align="flex-start">
										<Group gap="sm" align="center">
											<ThemeIcon radius="xl" size="lg" variant="filled" style={{ backgroundColor: contact.Colour || "var(--mantine-color-blue-light)" }}>
												<IconBuildingStore size={16} />
											</ThemeIcon>
											<Stack gap={2}>
												<Text fw="600">{contact.FullName}</Text>
												{contact.Role && <Text size="xs" c="dimmed">{contact.Role}</Text>}
											</Stack>
										</Group>
									</Group>
									<SimpleGrid cols={isMobile ? 1 : 2} mt="xs">
										{contact.Email && <Text size="sm">Email: {contact.Email}</Text>}
										{contact.PhoneNumber && <Text size="sm">Phone: {contact.PhoneNumber}</Text>}
									</SimpleGrid>
								</Paper>
							))}
						</Stack>
					) : (
						<Text size="sm" c="dimmed">No contacts have been added.</Text>
					)}
					<Divider my="md" />

					<SimpleGrid cols={isMobile ? 1 : 2} mb="md">
						<CrudUserDate label="Created" userId={vendor.CreatedBy.UserId} userName={vendor.CreatedBy.Name} date={vendor.Created} />
						{vendor.UpdatedBy && !vendor.Deleted && <>
							<CrudUserDate label="Updated" userId={vendor.UpdatedBy.UserId} userName={vendor.UpdatedBy.Name} date={vendor.Updated} />
						</>}
						{vendor.DeletedBy && <>
							<CrudUserDate label="Deleted" userId={vendor.DeletedBy.UserId} userName={vendor.DeletedBy.Name} date={vendor.Deleted} />
						</>}
					</SimpleGrid>

					<VendorAssetsList assets={vendor.Assets} />
					<VendorWorkOrderList vendorId={vendor.VendorId} onSelect={handleWorkOrderSelected} />
				</Container>
			</ScrollArea>
		</Paper>
	</>;
};
